import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Envelope, Phone, Trash, Eye, EyeClosed, Calendar, User, SpinnerGap, CheckCircle, Warning, Briefcase, Users, GraduationCap, Download, CalendarBlank, Star } from "@phosphor-icons/react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { EventsManager } from "../components/admin/EventsManager";
import { ReviewsManager } from "../components/admin/ReviewsManager";
import { useSeo } from "../hooks/useSeo";

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("admin_token");
  return fetch(url, { ...options, headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` } });
};

const AdminPage = () => {
  const [messages, setMessages] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [coworkingApplications, setCoworkingApplications] = useState([]);
  const [educationMembers, setEducationMembers] = useState([]);
  const [educationStats, setEducationStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("messages");
  const [password, setPassword] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState("");
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  useSeo({ title: "Admin | JS Blond Salon", description: "Interner Verwaltungsbereich", path: "/admin", noindex: true });

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password })
      });
      if (!response.ok) {
        setError("Falsches Passwort");
        return;
      }
      const data = await response.json();
      localStorage.setItem("admin_token", data.token);
      setIsAuthenticated(true);
    } catch (err) {
      setError("Verbindungsfehler. Bitte erneut versuchen.");
    }
  };

  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem("admin_token");
      if (!token) return;
      try {
        const response = await fetch(`${API_URL}/api/admin/verify`, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        if (response.ok) {
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem("admin_token");
        }
      } catch {
        localStorage.removeItem("admin_token");
      }
    };
    verifyToken();
  }, [API_URL]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [messagesRes, bookingsRes, coworkingRes, educationRes, eduStatsRes] = await Promise.all([
        authFetch(`${API_URL}/api/contact`),
        authFetch(`${API_URL}/api/seminars/bookings`),
        authFetch(`${API_URL}/api/coworking/applications`),
        authFetch(`${API_URL}/api/education/members`),
        authFetch(`${API_URL}/api/education/stats`)
      ]);

      if (messagesRes.status === 401) {
        handleLogout();
        return;
      }
      
      const messagesData = await messagesRes.json();
      const bookingsData = await bookingsRes.json();
      const coworkingData = await coworkingRes.json();
      const educationData = await educationRes.json();
      const eduStatsData = await eduStatsRes.json();
      
      setMessages(messagesData || []);
      setBookings(bookingsData.bookings || []);
      setCoworkingApplications(coworkingData || []);
      setEducationMembers(educationData || []);
      setEducationStats(eduStatsData);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setIsLoading(false);
  };

  const markAsRead = async (messageId) => {
    try {
      await authFetch(`${API_URL}/api/contact/${messageId}/read`, {
        method: "PATCH"
      });
      setMessages(messages.map(m => 
        m.id === messageId ? { ...m, read: true } : m
      ));
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm("Nachricht wirklich löschen?")) return;
    
    try {
      await authFetch(`${API_URL}/api/contact/${messageId}`, {
        method: "DELETE"
      });
      setMessages(messages.filter(m => m.id !== messageId));
    } catch (error) {
      console.error("Error deleting message:", error);
    }
  };

  const updateApplicationStatus = async (applicationId, newStatus) => {
    try {
      await authFetch(`${API_URL}/api/coworking/applications/${applicationId}/status?status=${newStatus}`, {
        method: "PATCH"
      });
      setCoworkingApplications(coworkingApplications.map(a => 
        a.id === applicationId ? { ...a, status: newStatus } : a
      ));
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const deleteApplication = async (applicationId) => {
    if (!window.confirm("Bewerbung wirklich löschen?")) return;
    
    try {
      await authFetch(`${API_URL}/api/coworking/applications/${applicationId}`, {
        method: "DELETE"
      });
      setCoworkingApplications(coworkingApplications.filter(a => a.id !== applicationId));
    } catch (error) {
      console.error("Error deleting application:", error);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem("admin_token");
    setPassword("");
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#F9F7F2]">
        <Navigation />
        <section className="pt-32 pb-20">
          <div className="container-padding">
            <div className="max-w-md mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white p-8 rounded-2xl border border-[#E6E2D6]"
              >
                <h1
                  className="text-2xl font-medium text-center mb-6"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Admin Login
                </h1>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Passwort eingeben"
                      className="w-full px-4 py-3 border border-[#E6E2D6] rounded-lg focus:outline-none focus:border-[#D4AF37]"
                      data-testid="admin-password-input"
                    />
                  </div>
                  {error && (
                    <p className="text-red-500 text-sm text-center">{error}</p>
                  )}
                  <button
                    type="submit"
                    data-testid="admin-login-btn"
                    className="w-full bg-[#2C2C2C] text-white py-3 rounded-full font-medium hover:bg-[#D4AF37] transition-colors"
                  >
                    Einloggen
                  </button>
                </form>
              </motion.div>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      <Navigation />
      
      <section className="pt-32 pb-20">
        <div className="container-padding">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div>
              <h1
                className="text-3xl font-medium"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Admin Dashboard
              </h1>
              <p className="text-[#2C2C2C]/60">Nachrichten, Buchungen, Community & Events verwalten</p>
            </div>
            <button
              onClick={handleLogout}
              className="text-sm text-[#2C2C2C]/60 hover:text-red-500 transition-colors"
            >
              Ausloggen
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mb-8 flex-wrap">
            <button
              onClick={() => setActiveTab("messages")}
              data-testid="admin-tab-messages"
              className={`px-6 py-3 rounded-full font-medium transition-colors ${
                activeTab === "messages"
                  ? "bg-[#2C2C2C] text-white"
                  : "bg-white text-[#2C2C2C] border border-[#E6E2D6] hover:bg-[#F9F7F2]"
              }`}
            >
              <span className="flex items-center gap-2">
                <Envelope size={18} />
                Nachrichten
                {messages.filter(m => !m.read).length > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {messages.filter(m => !m.read).length}
                  </span>
                )}
              </span>
            </button>
            <button
              onClick={() => setActiveTab("bookings")}
              data-testid="admin-tab-bookings"
              className={`px-6 py-3 rounded-full font-medium transition-colors ${
                activeTab === "bookings"
                  ? "bg-[#2C2C2C] text-white"
                  : "bg-white text-[#2C2C2C] border border-[#E6E2D6] hover:bg-[#F9F7F2]"
              }`}
            >
              <span className="flex items-center gap-2">
                <Calendar size={18} />
                Buchungen
                {bookings.length > 0 && (
                  <span className="bg-[#D4AF37] text-white text-xs px-2 py-0.5 rounded-full">
                    {bookings.length}
                  </span>
                )}
              </span>
            </button>
            <button
              onClick={() => setActiveTab("coworking")}
              data-testid="admin-tab-coworking"
              className={`px-6 py-3 rounded-full font-medium transition-colors ${
                activeTab === "coworking"
                  ? "bg-[#2C2C2C] text-white"
                  : "bg-white text-[#2C2C2C] border border-[#E6E2D6] hover:bg-[#F9F7F2]"
              }`}
            >
              <span className="flex items-center gap-2">
                <Briefcase size={18} />
                Co-Working
                {coworkingApplications.filter(a => a.status === "neu").length > 0 && (
                  <span className="bg-[#9A8C73] text-white text-xs px-2 py-0.5 rounded-full">
                    {coworkingApplications.filter(a => a.status === "neu").length}
                  </span>
                )}
              </span>
            </button>
            <button
              onClick={() => setActiveTab("education")}
              data-testid="admin-tab-education"
              className={`px-6 py-3 rounded-full font-medium transition-colors ${
                activeTab === "education"
                  ? "bg-[#2C2C2C] text-white"
                  : "bg-white text-[#2C2C2C] border border-[#E6E2D6] hover:bg-[#F9F7F2]"
              }`}
            >
              <span className="flex items-center gap-2">
                <GraduationCap size={18} />
                Education
                {educationStats && educationStats.total > 0 && (
                  <span className="bg-[#D4AF37] text-white text-xs px-2 py-0.5 rounded-full">
                    {educationStats.verified}
                  </span>
                )}
              </span>
            </button>
            <button
              onClick={() => setActiveTab("events")}
              data-testid="admin-tab-events"
              className={`px-6 py-3 rounded-full font-medium transition-colors ${
                activeTab === "events"
                  ? "bg-[#2C2C2C] text-white"
                  : "bg-white text-[#2C2C2C] border border-[#E6E2D6] hover:bg-[#F9F7F2]"
              }`}
            >
              <span className="flex items-center gap-2">
                <CalendarBlank size={18} />
                Events
              </span>
            </button>
            <button
              onClick={() => setActiveTab("reviews")}
              data-testid="admin-tab-reviews"
              className={`px-6 py-3 rounded-full font-medium transition-colors ${
                activeTab === "reviews"
                  ? "bg-[#2C2C2C] text-white"
                  : "bg-white text-[#2C2C2C] border border-[#E6E2D6] hover:bg-[#F9F7F2]"
              }`}
            >
              <span className="flex items-center gap-2">
                <Star size={18} />
                Bewertungen
              </span>
            </button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <SpinnerGap size={40} className="animate-spin text-[#D4AF37]" />
            </div>
          ) : (
            <>
              {/* Messages Tab */}
              {activeTab === "messages" && (
                <div className="space-y-4">
                  {messages.length === 0 ? (
                    <div className="bg-white p-8 rounded-xl border border-[#E6E2D6] text-center">
                      <Envelope size={40} className="mx-auto text-[#E6E2D6] mb-4" />
                      <p className="text-[#2C2C2C]/60">Keine Nachrichten vorhanden</p>
                    </div>
                  ) : (
                    messages.map((message) => (
                      <motion.div
                        key={message.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`bg-white p-6 rounded-xl border ${
                          message.read ? "border-[#E6E2D6]" : "border-[#D4AF37] border-2"
                        }`}
                      >
                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                          <div className="flex-grow">
                            <div className="flex items-center gap-3 mb-2">
                              <User size={20} className="text-[#9A8C73]" />
                              <span className="font-medium">{message.name}</span>
                              {!message.read && (
                                <span className="bg-[#D4AF37] text-white text-xs px-2 py-0.5 rounded-full">
                                  Neu
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm text-[#2C2C2C]/60 mb-3">
                              <span className="flex items-center gap-1">
                                <Envelope size={14} />
                                <a href={`mailto:${message.email}`} className="hover:text-[#D4AF37]">
                                  {message.email}
                                </a>
                              </span>
                              {message.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone size={14} />
                                  <a href={`tel:${message.phone}`} className="hover:text-[#D4AF37]">
                                    {message.phone}
                                  </a>
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Calendar size={14} />
                                {formatDate(message.created_at)}
                              </span>
                            </div>
                            <p className="text-[#2C2C2C]/80 bg-[#F9F7F2] p-4 rounded-lg">
                              {message.message}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            {!message.read && (
                              <button
                                onClick={() => markAsRead(message.id)}
                                className="p-2 text-[#9A8C73] hover:text-green-500 hover:bg-green-50 rounded-lg transition-colors"
                                title="Als gelesen markieren"
                              >
                                <Eye size={20} />
                              </button>
                            )}
                            <button
                              onClick={() => deleteMessage(message.id)}
                              className="p-2 text-[#9A8C73] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Löschen"
                            >
                              <Trash size={20} />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))
                  )}
                </div>
              )}

              {/* Bookings Tab */}
              {activeTab === "bookings" && (
                <div className="space-y-4">
                  {bookings.length === 0 ? (
                    <div className="bg-white p-8 rounded-xl border border-[#E6E2D6] text-center">
                      <Calendar size={40} className="mx-auto text-[#E6E2D6] mb-4" />
                      <p className="text-[#2C2C2C]/60">Keine Buchungen vorhanden</p>
                    </div>
                  ) : (
                    bookings.map((booking) => (
                      <motion.div
                        key={booking.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white p-6 rounded-xl border border-[#E6E2D6]"
                      >
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-3 mb-2">
                              <span className="font-medium">{booking.name}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                booking.payment_status === "paid"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-yellow-100 text-yellow-700"
                              }`}>
                                {booking.payment_status === "paid" ? (
                                  <span className="flex items-center gap-1">
                                    <CheckCircle size={12} weight="fill" />
                                    Bezahlt
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1">
                                    <Warning size={12} />
                                    Ausstehend
                                  </span>
                                )}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm text-[#2C2C2C]/60">
                              <span className="flex items-center gap-1">
                                <Envelope size={14} />
                                <a href={`mailto:${booking.email}`} className="hover:text-[#D4AF37]">
                                  {booking.email}
                                </a>
                              </span>
                              {booking.phone && (
                                <span className="flex items-center gap-1">
                                  <Phone size={14} />
                                  {booking.phone}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-[#D4AF37]">{booking.seminar_name}</p>
                            <p className="text-lg font-bold">{booking.amount} €</p>
                            <p className="text-xs text-[#2C2C2C]/50">
                              {formatDate(booking.created_at)}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ))
                  )}
                </div>
              )}

              {/* Coworking Tab */}
              {activeTab === "coworking" && (
                <div className="space-y-4">
                  {coworkingApplications.length === 0 ? (
                    <div className="bg-white p-8 rounded-xl border border-[#E6E2D6] text-center">
                      <Briefcase size={40} className="mx-auto text-[#E6E2D6] mb-4" />
                      <p className="text-[#2C2C2C]/60">Keine Co-Working Anfragen vorhanden</p>
                    </div>
                  ) : (
                    coworkingApplications.map((application) => (
                      <motion.div
                        key={application.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`bg-white p-6 rounded-xl border ${
                          application.status === "neu" ? "border-[#9A8C73] border-2" : "border-[#E6E2D6]"
                        }`}
                      >
                        <div className="flex flex-col gap-4">
                          {/* Header */}
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                              <User size={20} className="text-[#9A8C73]" />
                              <span className="font-medium text-lg">{application.name}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                application.status === "neu" ? "bg-[#9A8C73] text-white" :
                                application.status === "kontaktiert" ? "bg-blue-100 text-blue-700" :
                                application.status === "kennenlernen" ? "bg-yellow-100 text-yellow-700" :
                                application.status === "angenommen" ? "bg-green-100 text-green-700" :
                                "bg-red-100 text-red-700"
                              }`}>
                                {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                              </span>
                            </div>
                            <div className="flex gap-2">
                              <select
                                value={application.status}
                                onChange={(e) => updateApplicationStatus(application.id, e.target.value)}
                                className="text-sm border border-[#E6E2D6] rounded-lg px-3 py-1 focus:outline-none focus:border-[#D4AF37]"
                              >
                                <option value="neu">Neu</option>
                                <option value="kontaktiert">Kontaktiert</option>
                                <option value="kennenlernen">Kennenlernen</option>
                                <option value="angenommen">Angenommen</option>
                                <option value="abgelehnt">Abgelehnt</option>
                              </select>
                              <button
                                onClick={() => deleteApplication(application.id)}
                                className="p-2 text-[#9A8C73] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                title="Löschen"
                              >
                                <Trash size={18} />
                              </button>
                            </div>
                          </div>

                          {/* Contact Info */}
                          <div className="flex flex-wrap gap-4 text-sm text-[#2C2C2C]/60">
                            <a href={`tel:${application.phone}`} className="flex items-center gap-1 hover:text-[#D4AF37]">
                              <Phone size={14} />
                              {application.phone}
                            </a>
                            <a href={`mailto:${application.email}`} className="flex items-center gap-1 hover:text-[#D4AF37]">
                              <Envelope size={14} />
                              {application.email}
                            </a>
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {formatDate(application.created_at)}
                            </span>
                          </div>

                          {/* Details Grid */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-[#F9F7F2] p-4 rounded-lg">
                            <div>
                              <p className="text-xs text-[#2C2C2C]/50 mb-1">Aktuelle Situation</p>
                              <p className="text-sm font-medium text-[#2C2C2C]">{application.current_work}</p>
                            </div>
                            <div>
                              <p className="text-xs text-[#2C2C2C]/50 mb-1">Eigene Kunden</p>
                              <p className="text-sm font-medium text-[#2C2C2C]">{application.has_customers}</p>
                            </div>
                            <div>
                              <p className="text-xs text-[#2C2C2C]/50 mb-1">Gewünschte Tage</p>
                              <p className="text-sm font-medium text-[#2C2C2C]">{application.days_per_week}</p>
                            </div>
                          </div>

                          {/* Interests */}
                          {application.interests && application.interests.length > 0 && (
                            <div>
                              <p className="text-xs text-[#2C2C2C]/50 mb-2">Interessiert an:</p>
                              <div className="flex flex-wrap gap-2">
                                {application.interests.map((interest, i) => (
                                  <span key={i} className="text-xs bg-[#D4AF37]/10 text-[#9A8C73] px-2 py-1 rounded">
                                    {interest}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Message */}
                          {application.message && (
                            <div>
                              <p className="text-xs text-[#2C2C2C]/50 mb-1">Nachricht:</p>
                              <p className="text-sm text-[#2C2C2C]/80 bg-white p-3 rounded border border-[#E6E2D6]">
                                {application.message}
                              </p>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))
                  )}
                </div>
              )}

              {/* Education Tab */}
              {activeTab === "education" && (
                <div className="space-y-4">
                  {/* Stats */}
                  {educationStats && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                      <div className="bg-white p-4 rounded-xl border border-[#E6E2D6] text-center">
                        <p className="text-2xl font-bold text-[#2C2C2C]">{educationStats.total}</p>
                        <p className="text-xs text-[#2C2C2C]/50">Gesamt</p>
                      </div>
                      <div className="bg-white p-4 rounded-xl border border-[#E6E2D6] text-center">
                        <p className="text-2xl font-bold text-green-600">{educationStats.verified}</p>
                        <p className="text-xs text-[#2C2C2C]/50">Verifiziert</p>
                      </div>
                      <div className="bg-white p-4 rounded-xl border border-[#E6E2D6] text-center">
                        <p className="text-2xl font-bold text-yellow-600">{educationStats.pending}</p>
                        <p className="text-xs text-[#2C2C2C]/50">Ausstehend</p>
                      </div>
                      <div className="bg-white p-4 rounded-xl border border-[#E6E2D6] text-center">
                        <p className="text-2xl font-bold text-[#D4AF37]">{educationStats.from_postcard}</p>
                        <p className="text-xs text-[#2C2C2C]/50">Via Postkarte</p>
                      </div>
                    </div>
                  )}

                  {/* CSV Export */}
                  <div className="flex justify-end mb-4">
                    <button
                      onClick={async () => {
                        const res = await authFetch(`${API_URL}/api/education/members/export`);
                        if (!res.ok) return;
                        const blob = await res.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = "education_community.csv";
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                      data-testid="education-csv-export-btn"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-white text-sm rounded-full hover:bg-[#2C2C2C] transition-colors"
                    >
                      <Download size={16} />
                      CSV Export
                    </button>
                  </div>

                  {educationMembers.length === 0 ? (
                    <div className="bg-white p-8 rounded-xl border border-[#E6E2D6] text-center">
                      <GraduationCap size={40} className="mx-auto text-[#E6E2D6] mb-4" />
                      <p className="text-[#2C2C2C]/60">Noch keine Community-Mitglieder</p>
                    </div>
                  ) : (
                    educationMembers.map((member) => (
                      <motion.div
                        key={member.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`bg-white p-6 rounded-xl border ${
                          member.email_verified ? "border-green-200" : "border-yellow-200"
                        }`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-3 mb-2">
                              <User size={20} className="text-[#9A8C73]" />
                              <span className="font-medium text-lg">{member.first_name}</span>
                              <span className="text-sm text-[#2C2C2C]/50">({member.salon})</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                member.email_verified ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                              }`}>
                                {member.email_verified ? "Verifiziert" : "Ausstehend"}
                              </span>
                              {member.source === "postcard" && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-[#D4AF37]/10 text-[#D4AF37]">Postkarte</span>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm text-[#2C2C2C]/60">
                              <a href={`mailto:${member.email}`} className="flex items-center gap-1 hover:text-[#D4AF37]">
                                <Envelope size={14} /> {member.email}
                              </a>
                              <span>{member.zip_code} {member.city}</span>
                              <span className="flex items-center gap-1"><Calendar size={14} /> {formatDate(member.created_at)}</span>
                            </div>
                          </div>
                          <button
                            onClick={async () => {
                              if (!window.confirm("Mitglied löschen?")) return;
                              await authFetch(`${API_URL}/api/education/members/${member.id}`, { method: "DELETE" });
                              setEducationMembers(educationMembers.filter(m => m.id !== member.id));
                            }}
                            className="p-2 text-[#9A8C73] hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash size={18} />
                          </button>
                        </div>

                        {member.interests && member.interests.length > 0 && (
                          <div className="mt-3">
                            <div className="flex flex-wrap gap-2">
                              {member.interests.map((interest, i) => (
                                <span key={i} className="text-xs bg-[#D4AF37]/10 text-[#9A8C73] px-2 py-1 rounded">{interest}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {member.wishlist && (
                          <div className="mt-3">
                            <p className="text-xs text-[#2C2C2C]/50 mb-1">Wunsch:</p>
                            <p className="text-sm text-[#2C2C2C]/80 bg-[#F9F7F2] p-3 rounded">{member.wishlist}</p>
                          </div>
                        )}
                      </motion.div>
                    ))
                  )}
                </div>
              )}

              {/* Events Tab */}
              {activeTab === "events" && (
                <EventsManager apiUrl={API_URL} authFetch={authFetch} />
              )}

              {activeTab === "reviews" && (
                <ReviewsManager apiUrl={API_URL} authFetch={authFetch} />
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AdminPage;
